import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PUBLIC_FEATURE_MATRIX, PUBLIC_PLANS } from "@/config/publicPlans";
import {
  isPaidCompareRow,
  PlanCompareCell,
} from "@/features/public/components/PlanCompareCell";
import { effectiveVipLevel } from "@/domain/billing/planCapabilities";
import { isUiMockMode } from "@/config/features";
import {
  createBillingCheckoutSession,
  createBillingPortalSession,
  fetchBillingStatus,
} from "@/features/billing/api/billing";
import {
  clearBillingPendingPlan,
  peekBillingPendingPlan,
  setBillingPendingPlan,
} from "@/features/billing/billingPendingPlan";
import {
  billingReturnParamsToClear,
  hasBillingConfirmTicket,
  isBillingCancelReturn,
  isOrphanBillingReturnUrl,
} from "@/features/billing/billingReturnParams";
import {
  closeBillingTab,
  navigateBillingTab,
  openBillingUrlInSameTab,
  prepareBillingTab,
} from "@/features/billing/openBillingUrl";
import {
  formatPlanLabel,
} from "@/features/billing/formatBillingStatus";
import {
  BILLING_STATUS_QUERY_KEY,
  useBillingStatus,
} from "@/features/billing/hooks/useBillingStatus";
import { PageHeader } from "@/features/layout/components/PageHeader";
import { formatApiError } from "@/lib/http/error-handler";
import {
  PLAN_ACTION_CANCEL_SUBSCRIPTION,
  PLAN_ACTION_MANAGE_BILLING,
  PLAN_ACTION_MANAGE_SUBSCRIPTION,
  PLAN_ACTION_OPEN_BILLING_HERE,
  PLAN_CANCEL_DONE,
  PLAN_CANCEL_RENEWAL_NOTE,
  PLAN_CHECKOUT_NEW_TAB_HINT,
  PLAN_CHECKOUT_WEBHOOK_HINT,
  PLAN_CTA_LINE_PLACEHOLDER,
  PLAN_CTA_SUBSCRIBE,
  PLAN_ERR_POPUP_BLOCKED,
  PLAN_FEATURE_FOOTNOTE,
  PLAN_LEAD_CONFIRMING,
  PLAN_NAV_LABEL,
} from "@/features/billing/planCopy";

const PLANS = PUBLIC_PLANS;

const CHECKOUT_POLL_ATTEMPTS = 15;
const CHECKOUT_POLL_MS = 2000;

function planCtaLabel(planId: string, isCurrent: boolean): string {
  if (isCurrent) return "Current plan";
  if (planId === "pro") return PLAN_CTA_SUBSCRIBE;
  return PLAN_CTA_LINE_PLACEHOLDER;
}

function billingPortalLabel(subscribed: boolean): string {
  return subscribed ? PLAN_ACTION_MANAGE_SUBSCRIPTION : PLAN_ACTION_MANAGE_BILLING;
}

function clearBillingReturnSearchParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of billingReturnParamsToClear()) {
    next.delete(key);
  }
  return next;
}

export function CreditsPage() {
  const mock = isUiMockMode();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutParam = searchParams.get("checkout");
  const storedPendingPlan = peekBillingPendingPlan();
  const hasConfirmTicket = useMemo(
    () => hasBillingConfirmTicket(searchParams, storedPendingPlan),
    [searchParams, storedPendingPlan],
  );

  const { status } = useBillingStatus();
  const [confirmingCheckout, setConfirmingCheckout] = useState(
    () => !mock && hasConfirmTicket,
  );
  const [checkoutPollTimedOut, setCheckoutPollTimedOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [awaitingCheckout, setAwaitingCheckout] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [popupFallbackUrl, setPopupFallbackUrl] = useState<string | null>(null);

  useEffect(() => {
    if (mock || !isOrphanBillingReturnUrl(searchParams)) {
      return;
    }
    setConfirmingCheckout(false);
    setAwaitingCheckout(false);
    setSearchParams((prev) => clearBillingReturnSearchParams(prev), { replace: true });
  }, [mock, searchParams, setSearchParams]);

  useEffect(() => {
    if (mock || !isBillingCancelReturn(searchParams)) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: BILLING_STATUS_QUERY_KEY });
    setSearchParams((prev) => clearBillingReturnSearchParams(prev), { replace: true });
  }, [mock, searchParams, setSearchParams, queryClient]);

  const openStripeBillingInNewTab = async (
    fetchUrl: () => Promise<string | undefined>,
    options?: { awaitPlanChange?: boolean; planId?: string | null },
  ) => {
    setCheckoutError(null);
    setPopupFallbackUrl(null);
    const tab = prepareBillingTab();
    try {
      const url = await fetchUrl();
      if (!url) {
        closeBillingTab(tab);
        setCheckoutError("Stripe billing URL was not returned.");
        return;
      }
      if (tab && navigateBillingTab(tab, url)) {
        if (options?.awaitPlanChange && options.planId) {
          setBillingPendingPlan(options.planId);
          setPendingPlanId(options.planId);
          setAwaitingCheckout(true);
        }
        return;
      }
      closeBillingTab(tab);
      setPopupFallbackUrl(url);
      setCheckoutError(PLAN_ERR_POPUP_BLOCKED);
      if (options?.awaitPlanChange && options.planId) {
        setBillingPendingPlan(options.planId);
        setPendingPlanId(options.planId);
        setAwaitingCheckout(true);
      }
    } catch (e) {
      closeBillingTab(tab);
      setCheckoutError(formatApiError(e, "generic"));
    }
  };

  const onOpenBillingFallback = () => {
    if (!popupFallbackUrl) {
      return;
    }
    openBillingUrlInSameTab(popupFallbackUrl);
  };

  const effectivePendingPlan = pendingPlanId ?? peekBillingPendingPlan();
  const shouldPollCheckout =
    !mock &&
    ((awaitingCheckout && effectivePendingPlan != null) ||
      hasBillingConfirmTicket(searchParams, effectivePendingPlan));

  useEffect(() => {
    if (!shouldPollCheckout) {
      return;
    }
    let cancelled = false;
    setConfirmingCheckout(true);
    setCheckoutPollTimedOut(false);

    const poll = async () => {
      const targetPlanId = effectivePendingPlan;

      for (let attempt = 0; attempt < CHECKOUT_POLL_ATTEMPTS && !cancelled; attempt++) {
        try {
          const nextStatus = await fetchBillingStatus();
          const checkoutDone =
            targetPlanId != null
              ? nextStatus.subscribed && nextStatus.vip_level === targetPlanId
              : nextStatus.subscribed;
          if (checkoutDone) {
            setConfirmingCheckout(false);
            setAwaitingCheckout(false);
            setPendingPlanId(null);
            clearBillingPendingPlan();
            queryClient.setQueryData(BILLING_STATUS_QUERY_KEY, nextStatus);
            setSearchParams((prev) => clearBillingReturnSearchParams(prev), { replace: true });
            return;
          }
        } catch {
          /* keep polling until attempts exhausted */
        }
        await new Promise((resolve) => setTimeout(resolve, CHECKOUT_POLL_MS));
      }
      if (!cancelled) {
        setConfirmingCheckout(false);
        setAwaitingCheckout(false);
        setPendingPlanId(null);
        clearBillingPendingPlan();
        setCheckoutPollTimedOut(true);
        setSearchParams((prev) => clearBillingReturnSearchParams(prev), { replace: true });
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [
    shouldPollCheckout,
    mock,
    setSearchParams,
    queryClient,
    effectivePendingPlan,
  ]);

  const subscribed = status?.subscribed ?? false;
  const cancelAtPeriodEnd = status?.cancel_at_period_end ?? false;
  const canManageBilling = status?.can_manage_billing ?? false;
  const showBillingPortal = subscribed || canManageBilling;
  const currentPlanId = status ? effectiveVipLevel(status) : "starter";

  const onPlanAction = async (planId: string) => {
    if (mock) {
      alert("Mock mode — checkout is not sent to the API.");
      return;
    }
    if (planId !== "pro") {
      return;
    }
    setCheckoutError(null);
    setPopupFallbackUrl(null);
    try {
      const url = (await createBillingCheckoutSession({ vip_level: "pro" })).checkout_url;
      if (!url) {
        setCheckoutError("Stripe billing URL was not returned.");
        return;
      }
      setBillingPendingPlan("pro");
      openBillingUrlInSameTab(url);
    } catch (e) {
      setCheckoutError(formatApiError(e, "generic"));
    }
  };

  const onManageSubscription = async () => {
    if (mock) {
      alert("Mock mode — portal is not sent to the API.");
      return;
    }
    await openStripeBillingInNewTab(async () =>
      (await createBillingPortalSession({ intent: "manage" })).portal_url,
    );
  };

  const onCancelSubscription = async () => {
    if (mock) {
      alert("Mock mode — portal is not sent to the API.");
      return;
    }
    if (cancelAtPeriodEnd) {
      return;
    }
    setCheckoutError(null);
    setPopupFallbackUrl(null);
    try {
      const url = (await createBillingPortalSession({ intent: "cancel" })).portal_url;
      if (!url) {
        setCheckoutError("Stripe billing URL was not returned.");
        return;
      }
      openBillingUrlInSameTab(url);
    } catch (e) {
      setCheckoutError(formatApiError(e, "generic"));
    }
  };

  const lead = confirmingCheckout
    ? PLAN_LEAD_CONFIRMING
    : `You're on ${formatPlanLabel(currentPlanId)}.`;

  const showNewTabHint =
    awaitingCheckout && !hasBillingConfirmTicket(searchParams, effectivePendingPlan);

  return (
    <div className="billing-page flex min-h-0 flex-1 flex-col gap-5 overflow-auto pb-4">
      <PageHeader
        title={PLAN_NAV_LABEL}
        lead={lead}
        breadcrumbSuffix={PLAN_NAV_LABEL}
        className="page-head-row--compact shrink-0"
      />

      {confirmingCheckout ? (
        <p className="text-sm text-[var(--muted)]">
          {showNewTabHint
            ? PLAN_CHECKOUT_NEW_TAB_HINT
            : "Waiting for Stripe to confirm payment. This usually takes a few seconds."}
        </p>
      ) : null}

      {checkoutError ? (
        <p className="text-sm text-red-700">
          {checkoutError}
          {popupFallbackUrl ? (
            <>
              {" "}
              <button
                type="button"
                className="font-medium text-[var(--accent)] underline"
                onClick={onOpenBillingFallback}
              >
                {PLAN_ACTION_OPEN_BILLING_HERE}
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {checkoutPollTimedOut ? (
        <p className="text-sm text-[var(--muted)]">{PLAN_CHECKOUT_WEBHOOK_HINT}</p>
      ) : null}

      {checkoutParam === "cancel" ? (
        <p className="text-sm text-[var(--muted)]">Checkout was canceled.</p>
      ) : null}

      {showBillingPortal ? (
        <div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void onManageSubscription()}
          >
            {billingPortalLabel(subscribed)}
          </button>
        </div>
      ) : null}

      <div className="plans-grid">
        {PLANS.map((plan) => {
          const isCurrent = currentPlanId === plan.id || (subscribed && plan.id === "pro");
          const isStarterCard = plan.id === "starter";
          const showCancel = subscribed && plan.id === "pro";
          const hideCta = isStarterCard && !isCurrent;
          const buttonDisabled =
            confirmingCheckout || awaitingCheckout || isCurrent || isStarterCard;
          const cardClass = [
            "plan-card",
            plan.featured ? "plan-card--best" : "",
            isCurrent ? "plan-card--current" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={plan.id} className={cardClass}>
              <div className="plan-card-tag-slot">
                {plan.featured && !isCurrent ? (
                  <span className="plan-best-tag">Recommended</span>
                ) : null}
                {isCurrent ? (
                  <span className="plan-current-tag">Current plan</span>
                ) : null}
                {hideCta ? (
                  <span className="plan-current-tag plan-card-tag-placeholder" aria-hidden="true">
                    {PLAN_CTA_LINE_PLACEHOLDER}
                  </span>
                ) : null}
              </div>
              <h3>{plan.name}</h3>
              {plan.paid ? (
                <>
                  <div className="plan-price tabular-nums">
                    {plan.launchPrice}
                    <span className="text-sm font-normal text-[var(--muted)]">/mo</span>
                  </div>
                  <p className="plan-desc text-xs text-[var(--muted)]">
                    Billed monthly. Includes Bulk Export.
                  </p>
                </>
              ) : (
                <>
                  <div className="plan-price tabular-nums">{plan.launchPrice}</div>
                  <p className="plan-desc text-xs text-[var(--muted)]">
                    All downloads except Bulk Export Invoice PDFs
                  </p>
                </>
              )}
              <button
                type="button"
                className="btn-main plan-cta"
                disabled={buttonDisabled}
                aria-hidden={hideCta ? true : undefined}
                tabIndex={hideCta ? -1 : undefined}
                onClick={() => void onPlanAction(plan.id)}
              >
                {planCtaLabel(plan.id, isCurrent)}
              </button>
              {showCancel ? (
                <div className="plan-cancel">
                  {cancelAtPeriodEnd ? (
                    <p className="plan-cancel-done">{PLAN_CANCEL_DONE}</p>
                  ) : (
                    <button
                      type="button"
                      className="plan-cancel-link"
                      disabled={confirmingCheckout || awaitingCheckout}
                      onClick={() => void onCancelSubscription()}
                    >
                      {PLAN_ACTION_CANCEL_SUBSCRIPTION}
                    </button>
                  )}
                  <p className="plan-cancel-note">{PLAN_CANCEL_RENEWAL_NOTE}</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="panel overflow-x-auto p-4">
        <h3 className="mb-3 text-base font-semibold">Feature comparison</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-soft)] text-left">
              <th className="py-2 pr-3 font-semibold">Feature</th>
              <th className="py-2 pl-2 font-semibold">Price</th>
            </tr>
          </thead>
          <tbody>
            {PUBLIC_FEATURE_MATRIX.map((row) => (
              <tr
                key={row.feature}
                className={`border-b border-[var(--border-soft)]${isPaidCompareRow(row.pricing) ? " bg-[#fffbeb]" : ""}`}
              >
                <td className="py-2 pr-3 text-[var(--text-secondary)]">{row.feature}</td>
                <td className="py-2 pl-2">
                  <PlanCompareCell value={row.pricing} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-[var(--muted)]">{PLAN_FEATURE_FOOTNOTE}</p>
      </div>
    </div>
  );
}
